import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Chip, Group, Icon, Page, Row, Segmented } from '../ui/index.js';
import MetricChart from '../components/MetricChart.js';
import SleepView from '../components/SleepView.js';
import HighlightsView from '../components/HighlightsView.js';
import InsightCards from '../components/InsightCards.js';
import { getConcernBands, type ConcernBand } from '../api.js';
import { TREND_TYPES, DOMAINS, domainByKey, domainDefault, metricMeta, slotColor } from '../metrics.js';
import { useThemeMode } from '../lib/useThemeMode.js';
import { useUnitPref } from '../hooks/useUnitPref.js';
import { displayUnit } from '../lib/units.js';
import { autoBucket, rangeBounds, spanDays, type RangeKey } from '../lib/range.js';

const RANGE_OPTIONS: readonly { value: RangeKey | 'custom'; label: string }[] = [
  { value: '7d', label: '7D' },
  { value: '30d', label: '30D' },
  { value: '90d', label: '90D' },
  { value: '1y', label: '1Y' },
  { value: 'custom', label: 'Custom' },
];
const BUCKET_WORD: Record<string, string> = { raw: 'every reading', hour: 'hourly', day: 'daily', week: 'weekly', month: 'monthly' };
const DEFAULT_DOMAIN = 'heart';

/** yyyy-mm-dd for a date input default. */
function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export default function Trends(): JSX.Element {
  const mode = useThemeMode();
  const unitPref = useUnitPref();
  const [params] = useSearchParams();
  const initialDomain = ((): string => {
    const m = params.get('metric');
    if (m === 'sleep' || m === 'highlights') return m;
    return m && domainByKey(m) ? m : DEFAULT_DOMAIN;
  })();
  const [activeDomain, setActiveDomain] = useState<string>(initialDomain);
  const [selected, setSelected] = useState<string[]>(() => domainDefault(domainByKey(initialDomain) ? initialDomain : DEFAULT_DOMAIN));
  const [showAll, setShowAll] = useState(false);
  const [range, setRange] = useState<RangeKey | 'custom'>('30d');
  const [customFrom, setCustomFrom] = useState(() => isoDate(new Date(Date.now() - 30 * 86_400_000)));
  const [customTo, setCustomTo] = useState(() => isoDate(new Date()));
  const [showConcerns, setShowConcerns] = useState(false);
  const [concernBands, setConcernBands] = useState<ConcernBand[]>([]);

  const bounds = useMemo(() => {
    if (range === 'custom') {
      const from = new Date(`${customFrom}T00:00:00`);
      const toDay = new Date(`${customTo}T23:59:59`);
      const to = toDay > new Date() ? new Date() : toDay;
      return { from: from.toISOString(), to: to.toISOString() };
    }
    return rangeBounds(range);
  }, [range, customFrom, customTo]);

  const validCustom = range !== 'custom' || new Date(customFrom) <= new Date(customTo);
  const bucket = autoBucket(spanDays(bounds.from, bounds.to));
  const series = useMemo(() => selected.map((type) => ({ type })), [selected]);

  // Concern overlays: fetch the episodes overlapping the visible range whenever
  // the toggle is on or the range moves; clear them when off.
  useEffect(() => {
    if (!showConcerns) {
      setConcernBands([]);
      return;
    }
    let alive = true;
    getConcernBands(bounds.from, bounds.to)
      .then((rows) => alive && setConcernBands(rows))
      .catch(() => alive && setConcernBands([]));
    return () => {
      alive = false;
    };
  }, [showConcerns, bounds.from, bounds.to]);

  const chartBands = useMemo(
    () => concernBands.map((b) => ({ from: b.openedAt, to: b.closedAt, label: b.title })),
    [concernBands],
  );

  function toggle(type: string): void {
    setSelected((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  }

  function pickDomain(key: string): void {
    setActiveDomain(key);
    setSelected(domainDefault(key));
    setShowAll(false);
  }

  const domain = domainByKey(activeDomain);
  const pickerTypes = showAll || !domain ? TREND_TYPES : domain.types;

  const tabsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Keep the active domain chip in view (deep links land on Sleep/Highlights).
    const row = tabsRef.current;
    const on = row?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (row && on && (on.offsetLeft + on.offsetWidth > row.scrollLeft + row.clientWidth || on.offsetLeft < row.scrollLeft)) {
      row.scrollLeft = on.offsetLeft - 16;
    }
  }, [activeDomain]);

  const tabs: { key: string; label: string }[] = [
    ...DOMAINS.map((d) => ({ key: d.key, label: d.label })),
    { key: 'sleep', label: 'Sleep' },
    { key: 'highlights', label: 'Highlights' },
  ];
  const tabLabel = tabs.find((t) => t.key === activeDomain)?.label ?? 'Trends';
  const isMetrics = activeDomain !== 'sleep' && activeDomain !== 'highlights';
  const selectedLabels = selected.map((t) => metricMeta(t).label);

  return (
    <Page title="Trends" sub={isMetrics ? 'Your measures over time' : tabLabel} wide className="hb-trends-page">
      <div className="hb-chiprow" role="tablist" aria-label="Metric domains" ref={tabsRef}>
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={activeDomain === t.key}
            className={`ui-chip${activeDomain === t.key ? ' ui-chip--on' : ''}`}
            onClick={() => (t.key === 'sleep' || t.key === 'highlights' ? setActiveDomain(t.key) : pickDomain(t.key))}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeDomain === 'sleep' && <SleepView />}
      {activeDomain === 'highlights' && <HighlightsView />}

      {isMetrics && (
        <div className="hb-trends">
          <div className="hb-trends__main">
            <section className="hb-chart" aria-label="Chart">
              <div className="hb-chart__head">
                <div>
                  <h2 className="hb-chart__title">
                    {selectedLabels.length === 0
                      ? 'Nothing selected'
                      : selectedLabels.length <= 2
                        ? selectedLabels.join(' & ')
                        : `${selectedLabels.length} measures`}
                  </h2>
                  <span className="hb-chart__sub">{BUCKET_WORD[bucket] ?? bucket}</span>
                </div>
                <Chip on={showConcerns} onClick={() => setShowConcerns((v) => !v)} label="Concern overlays">
                  Concerns
                </Chip>
              </div>
              <div className="hb-seg">
                <Segmented label="Range" value={range} options={RANGE_OPTIONS} onChange={setRange} />
              </div>
              {range === 'custom' && (
                <div className="hb-daterange">
                  <label className="hb-field">
                    <span>From</span>
                    <input type="date" value={customFrom} max={customTo} onChange={(e) => setCustomFrom(e.target.value)} />
                  </label>
                  <label className="hb-field">
                    <span>To</span>
                    <input
                      type="date"
                      value={customTo}
                      min={customFrom}
                      max={isoDate(new Date())}
                      onChange={(e) => setCustomTo(e.target.value)}
                    />
                  </label>
                </div>
              )}
              {validCustom ? (
                <MetricChart
                  series={series}
                  from={bounds.from}
                  to={bounds.to}
                  bucket={bucket}
                  unitPref={unitPref}
                  bands={showConcerns ? chartBands : undefined}
                />
              ) : (
                <div className="chart-shell">
                  <div className="state">
                    <div className="state__title">Check the date range</div>
                    <div className="state__hint">The start date needs to fall on or before the end date.</div>
                  </div>
                </div>
              )}
            </section>
          </div>

          <div className="hb-trends__side">
            <Group
              header={showAll || !domain ? 'All metrics' : `${domain.label} metrics`}
              action={domain ? { label: showAll ? `${domain.label} only` : 'Show all', onClick: () => setShowAll((v) => !v) } : undefined}
            >
              {pickerTypes.map((type) => {
                const meta = metricMeta(type);
                const unit = displayUnit(type, unitPref);
                const on = selected.includes(type);
                return (
                  <Row
                    key={type}
                    leading={
                      <span
                        className={`hb-check${on ? ' hb-check--on' : ''}`}
                        style={{ ['--c' as string]: slotColor(meta.slot, mode) }}
                        aria-hidden="true"
                      >
                        {on && <Icon name="check" size={14} />}
                      </span>
                    }
                    title={meta.label}
                    detail={unit || undefined}
                    onClick={() => toggle(type)}
                    ariaLabel={`${on ? 'Hide' : 'Show'} ${meta.label}`}
                    chevron={false}
                  />
                );
              })}
            </Group>
          </div>
        </div>
      )}

      {isMetrics && (
        <div className="hb-insights--grid">
          <InsightCards title="Insights" />
        </div>
      )}
    </Page>
  );
}
